<template>
  <CollaborativeEditable
    :input-name="id"
    class="component-module-wrapper"
    :class="[
      {
        'component-module-wrapper--active': active && mode === 'EDIT',
        'component-module-wrapper--not-draggable': !isDraggable,
        'component-module-wrapper--force-scrollbars': forceScrollbars && !hasModuleMask,
        'component-module-wrapper--drop-shadow': hasDropShadow,
      },
      moduleAnimationsEnabled ? moduleAnimationClasses(moduleData) : undefined,
    ]"
    :style="{
      // This css var is used inside modules for shape masking
      '--clip-path-id': canEditImage ? undefined : `url(#module-shape-${id})`,
    }"
  >
    <ModuleShapeMask
      v-if="hasModuleMask"
      :module-height="moduleHeight"
      :module-id="id"
      :module-width="moduleWidth"
      :shape="moduleData.options.shape"
      :border-type="moduleData.options.border && moduleData.options.border.type"
      :border-variation="moduleData.options.border && moduleData.options.border.variation"
      :background-opacity="moduleShapeMaskBackgroundOpacity"
    />
    <div
      ref="moduleContents"
      class="component-module-wrapper__module-contents"
      :class="{
        'component-module-wrapper__module-contents--allow-overflow': moduleData
          .type === 'placeholder' || moduleData.options.button_style === 'mystery-science',
        'component-module-wrapper__module-contents--mystery-science': isMysteryTheme,
      }"
    >
      <slot name="default" />
    </div>
    <RotateModule
      v-if="rotateMenuVisible"
      :append-to="getAfterModuleContainerRef(id)"
      :module-data="moduleData"
      :module-element="$el"
      :scale="scale"
      :canvas-name="canvasName"
    />
    <RotateModuleDegrees
      v-if="rotateMenuVisible"
      :append-to="getAfterModuleContainerRef(id)"
      :module-data="moduleData"
      :module-element="$el"
      :scale="scale"
    />
    <EditorMenu
      v-if="miniMenuVisible"
      :append-to="getAfterModuleContainerRef(id)"
      :module-id="id"
      :scale="scale"
      :trigger-element="$el"
      :trigger-additional-element-ids="rotateMenuVisible ? [`rotate-module-button-${id}`] : []"
      :in-zoom-container="canvasName !== 'notes'"
      :box-name="canvasName === 'notes' ? 'notes' : 'editor'"
      :update-menu-position-delay="25"
      id-prefix="mini-floating-menu"
      @position-changed="onEditorMenuPositionChanged"
    >
      <div
        class="mini-menu"
        :class="{ 'mini-menu--hidden': rotating }"
        :style="miniMenuStyle"
      >
        <div
          ref="miniMenuBasicPanel"
          class="mini-menu__basic-panel"
        >
          <NebulaButton
            :aria-label="$t('Close Edit Menu')"
            v-if="showEditMenu || showPositionPanel || showAnimationPanel
              || showLinkPanel || showVideoPanel"
            icon-left="arrow-left"
            type="flat"
            size="l"
            @click="onCloseEdit"
          />
          <span class="mini-menu__label">
            {{ miniMenuTitle }}
          </span>
          <div
            :class="{
              'mini-menu__panel-wrapper': showEditMenu,
            }"
            :style="showEditMenu ? miniMenuPanelWrapperStyle : undefined"
            ref="miniMenuEditPanel"
          >
            <NebulaTooltip
              v-if="!showEditMenu && moduleTypeHasEditMenu"
              class="mini-menu__button-wrapper"
              :position="miniMenuButtonTooltipDisplayTop ? 'top' : 'bottom'"
              :text="$t('Edit')"
            >
              <NebulaButton
                :aria-label="$t('Edit')"
                :aria-describedby="`mini-menu__edit-aria-description--${id}`"
                de-tracking-name="studio_module-mini-menu_edit-module-button"
                icon-left="edit"
                type="flat"
                size="l"
                @click="onEdit"
              />
              <span
                :id="`mini-menu__edit-aria-description--${id}`"
                class="mini-menu__edit-aria-description"
              >
                {{ $t('Show more editing options for this module') }}
              </span>
            </NebulaTooltip>

            <div
              class="mini-menu__edit-panel"
              v-if="showEditMenu"
            >
              <slot name="module-options" />
            </div>
          </div>
          <div
            v-if="viewInContentUrl"
            class="mini-menu__button-wrapper"
          >
            <a
              :href="viewInContentUrl"
              class="nebula-button nebula-button--ghost
                nebula-button--s mini-menu__view-in-content"
              target="_blank"
            >
              {{ $t('View in Atlas') }}
            </a>
          </div>
          <div
            :class="{
              'mini-menu__panel-wrapper': showVideoPanel,
            }"
            :style="showVideoPanel ? miniMenuPanelWrapperStyle : undefined"
            ref="miniMenuVideoPanel"
          >
            <NebulaTooltip
              class="mini-menu__button-wrapper"
              v-if="canvasName !== 'notes' && assetGroup === 'video' && !showVideoPanel"
              :position="miniMenuButtonTooltipDisplayTop ? 'top' : 'bottom'"
              :text="$t('Video Settings')"
            >
              <NebulaButton
                :aria-label="$t('Edit Video Settings')"
                de-tracking-name="studio_module-mini-menu_video-settings-button"
                icon-left="video"
                type="flat"
                size="l"
                @click="onVideoSettingsClick"
              />
            </NebulaTooltip>

            <VideoOptions
              v-if="showVideoPanel"
              :page="currentPage"
              :module-data="moduleData"
              :canvas-name="canvasName"
              class="mini-menu__video-panel"
            />
          </div>

          <div
            :class="{
              'mini-menu__panel-wrapper': showLinkPanel,
            }"
            :style="showLinkPanel ? miniMenuPanelWrapperStyle : undefined"
            ref="miniMenuLinkPanel"
          >
            <NebulaTitleTooltip
              class="mini-menu__button-wrapper"
              v-if="moduleHasLinkPanel && !showLinkPanel"
              :position="miniMenuButtonTooltipDisplayTop ? 'block-start' : 'block-end'"
              :text="$t('Link')"
            >
              <NebulaButton
                :aria-label="$t('Link')"
                de-tracking-name="studio_module-mini-menu_link-module-button"
                icon-left="link"
                type="flat"
                size="l"
                @click="onLinkClick"
              />
            </NebulaTitleTooltip>

            <ModuleLink
              v-if="showLinkPanel"
              :module-data="moduleData"
              :canvas-name="canvasName"
              class="mini-menu__link-panel"
            />
          </div>

          <div
            :class="{
              'mini-menu__panel-wrapper': showAnimationPanel,
            }"
            :style="showAnimationPanel ? miniMenuPanelWrapperStyle : undefined"
            ref="miniMenuAnimationPanel"
          >
            <NebulaTitleTooltip
              class="mini-menu__button-wrapper"
              v-if="allowModuleAnimation(moduleData) && !showAnimationPanel"
              :position="miniMenuButtonTooltipDisplayTop ? 'block-start' : 'block-end'"
              :text="$t('Animation')"
            >
              <NebulaButton
                :aria-label="$t('Animation')"
                de-tracking-name="studio_module-mini-menu_animation-module-button"
                icon-left="coding-animation"
                type="flat"
                size="l"
                @click="onAnimationClick"
              />
            </NebulaTitleTooltip>

            <ModuleAnimation
              v-if="showAnimationPanel"
              :module-data="moduleData"
              :canvas-name="canvasName"
              class="mini-menu__animation-panel"
            />
          </div>

          <NebulaTooltip
            v-if="testLink"
            class="mini-menu__button-wrapper"
            :position="miniMenuButtonTooltipDisplayTop ? 'top' : 'bottom'"
            :text="testLinkLabel"
          >
            <!-- The referrerpolicy here is to allow better url tracking -->
            <NebulaButton
              :aria-label="testLinkLabel"
              :de-tracking-name="trackingLabelName"
              :link="testLink.url"
              :referrerpolicy="testLink.type === 'player' && 'no-referrer-when-downgrade'"
              target="_blank"
              icon-left="new-window"
              type="flat"
              size="l"
              @click="onAnimationClick"
            />
          </NebulaTooltip>

          <NebulaTooltip
            class="mini-menu__button-wrapper"
            :position="miniMenuButtonTooltipDisplayTop ? 'top' : 'bottom'"
            :text="$t('Duplicate')"
          >
            <NebulaButton
              :aria-label="$t('Duplicate')"
              de-tracking-name="studio_module-mini-menu_duplicate-module-button"
              icon-left="copy"
              type="flat"
              size="l"
              @click="onDuplicate"
            />
          </NebulaTooltip>

          <NebulaTooltip
            class="mini-menu__button-wrapper"
            :position="miniMenuButtonTooltipDisplayTop ? 'top' : 'bottom'"
            :text="$t('Copy to Clipboard (Ctrl + C)')"
          >
            <NebulaButton
              :aria-label="$t('Copy to Clipboard')"
              de-tracking-name="studio_module-mini-menu_copy-module-button"
              icon-left="copy-to-clipboard"
              type="flat"
              size="l"
              @click="onCopy()"
            />
          </NebulaTooltip>

          <NebulaTooltip
            v-if="moduleTypeHasReplace"
            class="mini-menu__button-wrapper"
            :position="miniMenuButtonTooltipDisplayTop ? 'top' : 'bottom'"
            :text="$t('Replace')"
          >
            <AddModule
              :module-id="id"
              :allow-drag="false"
              :canvas-name="canvasName"
              theme="replace"
              :module-to-replace="moduleData"
              :scale="1"
            >
              <template #trigger-button="{ toggleMenu }">
                <NebulaButton
                  :aria-label="$t('Replace')"
                  de-tracking-name="studio_module-mini-menu_replace-module-button"
                  icon-left="refresh"
                  type="flat"
                  size="l"
                  @click="toggleMenu"
                />
              </template>
            </AddModule>
          </NebulaTooltip>

          <div
            :class="{
              'mini-menu__panel-wrapper': showPositionPanel,
            }"
            :style="showPositionPanel ? miniMenuPanelWrapperStyle : undefined"
            ref="miniMenuPositionPanel"
          >
            <NebulaTooltip
              class="mini-menu__button-wrapper"
              v-if="canvasName !== 'notes' && gridIsDisabled && !showPositionPanel"
              :position="miniMenuButtonTooltipDisplayTop ? 'top' : 'bottom'"
              :text="$t('Position')"
            >
              <NebulaButton
                :aria-label="$t('Position')"
                de-tracking-name="studio_module-mini-menu_position-module-button"
                icon-left="bring-to-front"
                type="flat"
                size="l"
                @click="onPositionClick"
              />
            </NebulaTooltip>

            <ModulePosition
              v-if="showPositionPanel"
              :id="id"
              :canvas-name="canvasName"
              class="mini-menu__position-panel"
            />
          </div>

          <NebulaTooltip
            class="mini-menu__button-wrapper"
            :position="miniMenuButtonTooltipDisplayTop ? 'top' : 'bottom'"
            :text="$t('Delete')"
          >
            <NebulaButton
              :aria-label="$t('Delete')"
              de-tracking-name="studio_module-mini-menu_delete-module-button"
              icon-left="trash"
              type="flat"
              size="l"
              @click="onDelete"
            />
          </NebulaTooltip>
        </div>
      </div>
    </EditorMenu>
  </CollaborativeEditable>
</template>

<script>
import { defineAsyncComponent } from 'vue';
import {
  NebulaButton,
  NebulaTooltip,
  NebulaTitleTooltip,
} from '@discoveryedu/nebula-components';
import { mapActions, mapState } from 'pinia';
import { get, throttle } from 'lodash-es';
import AddModule from '@/components/AddModule.vue';
import * as types from '@/lib/constants/store';
import { bus } from '@/lib/eventBus';
import { formatUrl } from '@/lib/utils/url';
import moduleAnimation from '@/mixins/moduleAnimation';
import imageMixin from '@/mixins/image';
import CollaborativeEditable from '@/components/CollaborativeEditable.vue';
import EditorMenu from '@/components/EditorMenu.vue';
import ModuleShapeMask from '@/components/ModuleShapeMask.vue';
import * as appConstants from '@/lib/constants/app';
import { getEnv } from '@/lib/api';
import {
  useAssetStore,
  useEditorStore,
  useToastStore,
  useUserStore,
} from '@/stores';

// These are only used in EDIT mode
const ModuleAnimation = defineAsyncComponent(() => import('@/components/ModuleAnimation.vue'));
const ModuleLink = defineAsyncComponent(() => import('@/components/ModuleLink.vue'));
const ModulePosition = defineAsyncComponent(() => import('@/components/ModulePosition.vue'));
const RotateModule = defineAsyncComponent(() => import('@/components/RotateModule.vue'));
const RotateModuleDegrees = defineAsyncComponent(() => import('@/components/RotateModuleDegrees.vue'));
const VideoOptions = defineAsyncComponent(() => import('@/components/VideoOptions.vue'));

export default {
  name: 'ModuleWrapper',
  components: {
    AddModule,
    CollaborativeEditable,
    ModuleShapeMask,
    ModuleAnimation,
    ModuleLink,
    ModulePosition,
    VideoOptions,
    NebulaButton,
    NebulaTooltip,
    NebulaTitleTooltip,
    EditorMenu,
    RotateModule,
    RotateModuleDegrees,
  },
  mixins: [moduleAnimation, imageMixin],
  props: {
    active: Boolean,
    canvasName: {
      type: String,
      required: true,
    },
    mode: {
      type: String,
      required: true,
    },
    moduleData: {
      type: Object,
      required: true,
    },
    moduleName: {
      type: String,
      default: null,
    },
    scale: {
      type: Number,
      default: 1,
    },
    id: {
      type: String,
      required: true,
    },
    getAfterModuleContainerRef: {
      type: Function,
      default: () => null,
    },
    moduleAnimationsEnabled: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      forceScrollbars: false,
      moduleHeight: 0,
      moduleWidth: 0,
      showEditMenu: false,
      showLinkPanel: false,
      showPositionPanel: false,
      showAnimationPanel: false,
      showVideoPanel: false,
      isDraggable: true,
      showMiniMenu: true,
      miniMenuButtonTooltipDisplayTop: true,
      rotating: false,
      miniMenuWidth: null,
      miniMenuHeight: null,
      optionsWidth: null,
      optionsHeight: null,
      autoResizeWrapper: false,
    };
  },
  created() {
    this.onScrollThrottled = throttle(this.onScroll, 100);
  },
  computed: {
    ...mapState(useEditorStore, [
      'currentPage',
      'draft',
      'gridMatrix',
      'connectedUsersColors',
      'connectedUsers',
      'session',
      'draftIsLesson',
      'draftType',
      'domains',
      'isLocked',
      'currentlyEditing',
      'gridIsDisabled',
      'isMysteryTheme',
    ]),
    ...mapState(useUserStore, [
      'isDeUser',
    ]),
    ...mapState(useAssetStore, [
      'assets',
    ]),
    miniMenuPanelWrapperStyle() {
      return { minWidth: `${this.miniMenuWidth}px` };
    },
    miniMenuStyle() {
      if (!this.panelRef) return undefined;
      const width = Math.max(this.miniMenuWidth, this.optionsWidth);
      const height = this.miniMenuHeight + this.optionsHeight;
      return {
        width: `${width}px`,
        height: `${height}px`,
      };
    },
    moduleTypeHasEditMenu() {
      // All modules have expanded menus available except interactive modules,
      // and the placeholder module when lesson options are unavailable
      const interactiveModules = [
        'multiple_selection',
        'prompt',
        'single_selection',
        'block',
      ];

      if (interactiveModules.includes(this.moduleData.type)) {
        return false;
      }

      if (this.moduleData.type === 'placeholder') {
        return this.draftIsLesson;
      }

      return true;
    },
    moduleTypeHasReplace() {
      return ['asset', 'upload'].includes(this.moduleData.type);
    },
    currentlyEditingUser() {
      return this.currentlyEditing(this.id);
    },
    currentlyEditingName() {
      return `${this.currentlyEditingUser.firstName} ${this.currentlyEditingUser.lastName}`;
    },
    currentlyEditingColor() {
      return this.currentlyEditingUser ? this.connectedUsersColors[this.currentlyEditingUser.clientId] : 'transparent';
    },
    hasDropShadow() {
      return [
        'speech_bubble_lower_left',
        'speech_bubble_lower_right',
        'title_bubble',
      ].includes(this.moduleData?.options?.shape);
    },
    hasModuleMask() {
      return this.moduleData?.options?.shape;
    },
    moduleShapeMaskBackgroundOpacity() {
      return this.canEditImage ? 0.6 : undefined;
    },
    asset() {
      return this.assets[this.moduleData.options.asset_id];
    },
    assetGroup() {
      return this.asset?.group;
    },
    testLinkLabel() {
      if (['button', 'advanced_button'].includes(this.moduleData.type)) {
        return this.$t('Open Link');
      }

      let assetType = this.asset?.type?.name;

      if (!assetType) return this.$t('Open Asset');

      // "Other" is not a friendly asset type name.
      if (assetType === 'Other') assetType = 'Document';

      // All asset types are localized in /learn/templates/hax_localization/asset_types.html;
      // these are interpolated so we don't have to create another list of every asset type
      // with "Open" in front.
      return this.$t('Open %(assetType)s', {
        assetType: this.$t(assetType),
      });
    },
    trackingLabelName() {
      if (!this.testLinkLabel) return '';
      const formattedLabel = this.testLinkLabel.toLowerCase().replace(/ /g, '-');
      return `studio_module-mini-menu_${formattedLabel}-button`;
    },
    moduleHasLinkPanel() {
      // All buttons have the option to link
      if (['button', 'advanced_button'].includes(this.moduleData.type)) {
        return true;
      }

      // Image assets can also be linked
      const assetId = get(this.moduleData, 'options.asset_id');
      if (assetId) {
        const asset = this.assets[assetId];
        return asset?.group === 'image';
      }

      return false;
    },
    testLink() {
      if (['button', 'advanced_button'].includes(this.moduleData.type)
        && get(this.moduleData.options, 'action') === 'link') {
        // Buttons that go to an external link can be tested
        return {
          url: formatUrl(this.moduleData.options.target),
          type: 'link',
        };
      }

      if (['asset', 'upload'].includes(this.moduleData.type) && get(this.moduleData.options, 'asset_id')) {
        // Assets (including My Content uploads) can be tested
        return {
          url: `${this.$appDomain}/player/${this.moduleData.options.asset_id}`,
          type: 'player',
        };
      }

      return null;
    },
    viewInContentUrl() {
      if (!this.isDeUser || !this.showEditMenu) return null;

      const assetId = get(this, 'moduleData.type') === 'asset' && get(this, 'moduleData.options.asset_id');
      if (!assetId) return null;

      // Studio content is authored in the US site rather than in a region-specific content site
      const appDomainUS = appConstants[`VUE_APP_${getEnv()}_APPURL_US`];

      // Create a url like https://atlas.discoveryeducation.com/assets/<ASSET_ID>
      return appDomainUS.replace('app', 'atlas').replace('learn', `assets/${assetId}`);
    },
    miniMenuVisible() {
      return this.active && this.mode === 'EDIT' && !this.isLocked(this.id) && this.showMiniMenu;
    },
    miniMenuTitle() {
      if (this.assetGroup === 'video') {
        return this.$t('Video');
      }
      if (get(this, 'moduleData.options.word_tracking.enabled')) {
        return this.$t('Word Tracking');
      }
      if (this.moduleData?.type === 'advanced_button') {
        return this.$t('Button');
      }
      return this.moduleName;
    },
    rotateMenuVisible() {
      return this.active && this.mode === 'EDIT' && this.canvasName !== 'notes'
        && this.gridIsDisabled && !this.isLocked(this.id);
    },
    panelRef() {
      const panels = {
        showEditMenu: this.$refs.miniMenuEditPanel,
        showPositionPanel: this.$refs.miniMenuPositionPanel,
        showAnimationPanel: this.$refs.miniMenuAnimationPanel,
        showLinkPanel: this.$refs.miniMenuLinkPanel,
        showVideoPanel: this.$refs.miniMenuVideoPanel,
      };

      const activePanelKey = Object.keys(panels).find(key => this[key]);
      return activePanelKey ? panels[activePanelKey] : null;
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.ADD_MODULE,
      types.DELETE_MODULE,
      types.UPDATE_CURRENTLY_EDITING,
      types.COPY,
      types.DUPLICATE_MODULE,
    ]),
    ...mapActions(useToastStore, [
      types.SET_STUDIO_TOAST,
    ]),
    onScroll() {
      bus.emit('module:scroll');
    },
    onCloseEdit() {
      this.closeMiniMenuPanels();
    },
    onCopy() {
      // Copy to Pinia (to ensure that the "Paste from Clipboard" option in the
      // Add Module menu will always work) and also copy to the device clipboard
      // (for pasting across browser tabs)
      if (this.active) {
        this[types.COPY]({
          module: this.moduleData,
          canvasName: this.canvasName,
        });
      }
    },
    onDuplicate() {
      this[types.DUPLICATE_MODULE]({
        module: this.moduleData,
        canvasName: this.canvasName,
      });
    },
    onDelete() {
      this[types.DELETE_MODULE]({ canvasName: this.canvasName, id: this.id });
    },
    onEdit() {
      this.closeMiniMenuPanels();
      this.showEditMenu = true;
      this.attachMiniMenuObserver();
    },
    onForceScrollbars({ moduleId, forceScrollbars }) {
      if (moduleId === this.id) {
        this.forceScrollbars = forceScrollbars;
      }
    },
    onMoveOrResize(moduleId) {
      if (moduleId === this.id) {
        this.updateModuleDimensions();
      }
    },
    onSetIsDraggable({ moduleId, isDraggable }) {
      if (moduleId === this.id) {
        this.isDraggable = isDraggable;
      }
    },
    onEditorMenuPositionChanged(context) {
      // Change tooltip position based on available space in the UI
      const panelRect = this.$refs.miniMenuBasicPanel.getBoundingClientRect();
      const optionsVisible = this.showEditMenu || this.showPositionPanel
        || this.showAnimationPanel || this.showLinkPanel || this.showVideoPanel;
      this.miniMenuButtonTooltipDisplayTop = optionsVisible
        ? (panelRect.top - panelRect.height) >= context.selected.box.top
        : (panelRect.bottom + panelRect.height) > context.selected.box.bottom;
    },
    closeMiniMenuPanels() {
      this.showEditMenu = false;
      this.showAnimationPanel = false;
      this.showPositionPanel = false;
      this.showLinkPanel = false;
      this.showVideoPanel = false;
      this.attachMiniMenuObserver();
    },
    onPositionClick() {
      this.closeMiniMenuPanels();
      this.showPositionPanel = true;
      this.attachMiniMenuObserver();
    },
    onLinkClick() {
      this.closeMiniMenuPanels();
      this.showLinkPanel = true;
      this.attachMiniMenuObserver();
    },
    onAnimationClick() {
      this.closeMiniMenuPanels();
      this.showAnimationPanel = true;
      this.attachMiniMenuObserver();
    },
    onVideoSettingsClick() {
      this.closeMiniMenuPanels();
      this.showVideoPanel = true;
      this.attachMiniMenuObserver();
    },
    updateModuleDimensions() {
      this.$nextTick(() => {
        this.moduleWidth = this.$el.offsetWidth;
        this.moduleHeight = this.$el.offsetHeight;
      });
    },
    addRotationHandlers() {
      bus.on('module:rotation-start', this.onRotationStart);
      bus.on('module:rotation-end', this.onRotationEnd);
    },
    removeRotationHandlers() {
      bus.off('module:rotation-start', this.onRotationStart);
      bus.off('module:rotation-end', this.onRotationEnd);
    },
    onRotationStart(args) {
      if (args.id !== this.id) return;
      this.rotating = true;
    },
    onRotationEnd(args) {
      if (args.id !== this.id) return;
      this.rotating = false;
    },
    setMiniMenuDimensions() {
      const miniMenu = this.$refs.miniMenuBasicPanel;
      if (miniMenu) {
        const miniMenuRect = miniMenu.getBoundingClientRect();
        this.miniMenuWidth = miniMenuRect.width;
        this.miniMenuHeight = miniMenuRect.height;
      }

      if (this.panelRef) {
        const panelRect = this.panelRef.getBoundingClientRect();
        this.optionsWidth = panelRect.width;
        this.optionsHeight = panelRect.height;
      }
    },
    attachMiniMenuObserver() {
      this.$nextTick(() => {
        this.setMiniMenuDimensions();

        if (this.miniMenuResizeObserver) {
          this.miniMenuResizeObserver.disconnect();
        }

        const miniMenu = this.$refs.miniMenuBasicPanel;

        this.miniMenuResizeObserver = new ResizeObserver(this.setMiniMenuDimensions);

        // Observe the referenced elements
        if (miniMenu) {
          this.miniMenuResizeObserver.observe(miniMenu);
        }
        if (this.panelRef) {
          this.miniMenuResizeObserver.observe(this.panelRef);
        }
      });
    },
  },
  watch: {
    active(newVal) {
      if (newVal) {
        this.showMiniMenu = true;
        this.addRotationHandlers();
      } else {
        // If we've deactivated this module, close the edit menu
        this.onCloseEdit();
        this.removeRotationHandlers();
        this.rotating = false;
      }
    },
    'moduleData.options': {
      handler() {
        // If module data (other than the layout) changes, reset the scrollbars
        this.forceScrollbars = false;
      },
      deep: true,
    },
  },
  mounted() {
    bus.on('module:set-is-draggable', this.onSetIsDraggable);
    bus.on('module:force-scrollbars', this.onForceScrollbars);
    bus.on('moved', this.onMoveOrResize);
    bus.on('resized', this.onMoveOrResize);
    bus.on('copyModule', this.onCopy);

    if (window.ResizeObserver) {
      this.modResizeObserver = new ResizeObserver(this.updateModuleDimensions);
      this.modResizeObserver.observe(this.$el);
    }

    const { moduleContents } = this.$refs;
    if (moduleContents) {
      moduleContents.addEventListener('scroll', this.onScrollThrottled);
    }

    // On first load, modules may not be sized correctly yet. Wait until the grid library
    // sizes them to update module dimensions
    setTimeout(() => {
      this.updateModuleDimensions();
    }, 1);
  },
  beforeUnmount() {
    bus.off('module:set-is-draggable', this.onSetIsDraggable);
    bus.off('module:force-scrollbars', this.onForceScrollbars);
    bus.off('moved', this.onMoveOrResize);
    bus.off('resized', this.onMoveOrResize);
    bus.off('copyModule', this.onCopy);
    this.removeRotationHandlers();

    if (this.modResizeObserver) {
      this.modResizeObserver.unobserve(this.$el);
      this.modResizeObserver = null;
    }

    if (this.miniMenuResizeObserver) {
      this.miniMenuResizeObserver.disconnect();
    }

    if (this.moduleOptionsObserver) {
      this.moduleOptionsObserver.disconnect();
    }

    const { moduleContents } = this.$refs;
    if (moduleContents) {
      moduleContents.removeEventListener('scroll', this.onScrollThrottled);
    }
  },
};
</script>

<style lang="stylus">
.component-module-wrapper {
  border-radius: 6px;
  box-sizing: border-box;
  min-height: $nebula-space-4x;
  min-width: $nebula-space-4x;
  height: 100%;
  position: relative;

  &.component-module-wrapper--active {
    // Using box-shadow instead of border to avoid a jumpy layout
    box-shadow: 0px 0px 0px 2px #4DBAFF;
  }

  .component-module-wrapper__module-contents {
    border-radius: 6px;
    font-family: 'Proxima Nova Soft', 'Proxima Vara', Arial, sans-serif;
    font-feature-settings: 'liga' 0, 'ss01' 1;
    height: 100%;
    overflow: auto;

    &::-webkit-scrollbar {
      width: $nebula-space-1x;
    }

    &::-webkit-scrollbar-track {
      background: $nebula-color-white;
      border-radius: 0 $nebula-border-radius-small $nebula-border-radius-small 0;
    }

    &::-webkit-scrollbar-thumb {
      background: $nebula-color-white;
      border-radius: $nebula-border-radius-small;
    }

    &::-webkit-scrollbar-thumb:hover {
      background: $nebula-color-platform-neutral-700;
    }

    &:hover {
      &::-webkit-scrollbar-thumb {
        background: $nebula-color-platform-neutral-500;
      }
    }

    &--allow-overflow { // .component-module-wrapper__module-contents--allow-overflow
      overflow: visible;
    }

    // This is used inside each module rather than in this file
    &--shape-mask {
      clip-path: var(--clip-path-id);
      overflow: hidden;
    }

    &--mystery-science {
      border-radius: 0;
    }

  }

  &.component-module-wrapper--force-scrollbars .component-module-wrapper__module-contents {
    overflow-y: scroll;
  }

  &--drop-shadow {
    filter: drop-shadow(0px 4px 25px rgba(0, 0, 0, 0.1));
  }
}

.mini-menu {
  background-color: $nebula-color-white;
  border-radius: $nebula-border-radius-badge-default;
  box-shadow: 0px 2px 4px rgba($nebula-color-black, 0.25);
  opacity: 1;
  transition: opacity 0.2s ease-in-out;

  &--hidden {
    opacity: 0;
  }

  .mini-menu__basic-panel {
    align-items: flex-start;
    display: flex;
    flex-wrap: nowrap;
    padding: $nebula-space-quarter;
    width: max-content;

    > div, > span, > button {
      align-items: center;
    }

    html[dir="rtl"] & {
      .nebula__icon {
        transform: scaleX(-1);
      }
    }
  }

  .mini-menu__label {
    color: $nebula-color-interactive-blue-300;
    display: block;
    font-weight: bold;
    line-height: 1;
    padding: 15px 16px;
    white-space: nowrap;
  }

  &__edit-aria-description {
    display: none;
  }

  .mini-menu__icon {
    display: block;
    fill: $comet-text-color-primary-on-light;
    height: 20px;
    width: 20px;
  }

  .mini-menu__edit-panel {
    padding: $nebula-space-2x;
  }

  .mini-menu__position-panel,
  .mini-menu__animation-panel,
  .mini-menu__link-panel,
  .mini-menu__video-panel
  {
    padding: $nebula-space-1x $nebula-space-2x $nebula-space-2x $nebula-space-2x;
  }

  .mini-menu__button-wrapper {
    align-items: center;
    display: flex;
    position: relative;
  }

  &__view-in-content {
    margin-right: $nebula-space-half;
    margin-top: $nebula-space-1x;
  }

  &__panel-wrapper {
    left: 0;
    position: absolute;
    top: 54px;
  }
}
</style>
